import { Injectable, inject, signal } from '@angular/core';
import { fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UpqueueAdapter } from '../api/adapters/upqueue.adapter';
import { UpqueueService } from './upqueue.service';

/**
 * Detects the device's online/offline state via `navigator.onLine` and browser
 * `online`/`offline` events. On reconnect, automatically triggers
 * {@link UpqueueAdapter.transmitAll} when the queue has pending submissions
 * so process servers do not need to manually tap the Upqueue tab.
 *
 * @remarks
 * Initialised synchronously from `navigator.onLine` — no async startup race.
 * Subscriptions are cleaned up automatically via `takeUntilDestroyed()`.
 */
@Injectable({ providedIn: 'root' })
export class NetworkService {
  /** `true` when the device has an active network connection. */
  readonly isOnline = signal<boolean>(navigator.onLine);

  private readonly upqueueAdapter = inject(UpqueueAdapter);
  private readonly upqueueService = inject(UpqueueService);

  constructor() {
    fromEvent(window, 'offline')
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.isOnline.set(false));

    fromEvent(window, 'online')
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.isOnline.set(true);
        this.tryAutoTransmit();
      });

    // iOS WKWebView may delay the `online` event 3–10 s after airplane mode
    // is disabled. Listening for `visibilitychange` (app foregrounded) catches
    // the common case where the user turns off airplane mode while the app is
    // backgrounded and then brings the app to the foreground.
    fromEvent(document, 'visibilitychange')
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (!document.hidden && navigator.onLine) {
          const wasOffline = !this.isOnline();
          this.isOnline.set(true);
          if (wasOffline) this.tryAutoTransmit();
        }
      });
  }

  /**
   * Triggers {@link UpqueueAdapter.transmitAll} when connectivity is restored
   * and the queue contains pending submissions.
   *
   * @remarks
   * Guarded by {@link UpqueueAdapter.transmitting} — if a transmission is
   * already in progress, a second reconnect event is ignored until it finishes.
   */
  private tryAutoTransmit(): void {
    if (this.upqueueAdapter.transmitting() || this.upqueueService.pendingCount() === 0) return;
    this.upqueueAdapter.transmitAll().subscribe();
  }
}
